/**
 * 「初日から稼げる」の画面（/money/play-earnings・pages/money/PlayEarnPage.tsx）の判断と形（2026-10-26 money/earn-build）。
 * サーバー: GET /v1/money/earn（apps/server/src/modules/money/earnView.ts）。見せない人には 404 = ナビも出さない（MoneyShell の earnOn）。
 * 絵の元: docs/teams/money/earn-launch/dashboard-earnings-en.png（企画書 §5）。違い: 受け取りは Amazon のギフトカード（PayPal の出金は作っていない・
 *   founder 10/26「まずはSaraが試験的にやって、アマゾンギフトカードを送るよ」）= 「Available to withdraw」ではなく「送る前 / 送った」。
 *
 * このファイルは import を持たない（node --experimental-strip-types で型の除去だけで動かす = playEarn.test.ts）。
 */

export const PLAY_EARN_API = '/v1/money/earn';
export const PLAY_EARN_PATH = '/money/play-earnings';

export interface PlayEarnView {
  enabled: boolean;
  week: { start: string; end: string; closesOn: string };
  poolCents: number;
  centsPerPlay: number;
  creatorWeeklyCapCents: number;
  holdDays: number;
  rateCents: number;
  split: boolean;
  poolUsedCents: number;
  /** netPlays = countedPlays − deducted − underReview（確認中のゲームの分は今週払わない）。サーバーの earnView.ts と同じ */
  mine: { countedPlays: number; notCounted: number; deducted: number; underReview: number; netPlays: number; estimateCents: number; capped: boolean };
  games: Array<{ projectId: string; name: string; plays: number; deducted: number; estimateCents: number; status: 'ok' | 'review' }>;
  onHoldCents: number;
  readyCents: number;
  sentCents: number;
  weeks: Array<{ week: string; amountCents: number; status: 'held' | 'ready' | 'sent' | 'voided'; holdUntil: string; sentOn: string | null }>;
  /**
   * 受け取り。country / store = 作り手が選んだ受け取りの国（ISO の 2 文字）とその国の Amazon（null = まだ選んでいない = 送れない）。
   * countries = 選べる国（その国の Amazon の店がある国だけ・サーバーの earnCountry.ts AMAZON_STORES）。2026-10-26 earn-build-2・
   * founder「Amazon のギフトカードは、その人の国の Amazon（米国は amazon.com・カナダは amazon.ca 等）で送る」
   */
  payout: { method: 'amazon_gift_card'; email: string; country: string | null; store: string | null; countries: Array<{ country: string; store: string }> };
}

/** 受け取りの国を選ぶ口（PUT・本文 { country: 'US' | null }）。応答 { country, store } */
export const PLAY_EARN_COUNTRY_API = '/v1/money/earn/country';

/**
 * 国の選ぶ欄の並び（表示の言語の名前の順）。名前はブラウザの Intl.DisplayNames（言語ごとに国の名前を持たない）。
 * 名前が引けない環境は国の記号のまま。label = "Canada — amazon.ca"（どの店で買うかを選ぶ時に見せる）
 */
export function countryOptions(countries: PlayEarnView['payout']['countries'], locale: string): Array<{ country: string; store: string; name: string; label: string }> {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    names = null;
  }
  return countries
    .map((c) => {
      const name = names?.of(c.country) ?? c.country;
      return { ...c, name, label: `${name} — ${c.store}` };
    })
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/** 入れ物の使った割合（0〜100・帯の長さ）。入れ物 0 = 0 */
export function poolPercent(v: Pick<PlayEarnView, 'poolCents' | 'poolUsedCents'>): number {
  if (v.poolCents <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((v.poolUsedCents / v.poolCents) * 1000) / 10));
}

/**
 * 1 プレイの額の表示（ドル・小数 3〜4 桁）。1¢ → "$0.010"・0.625¢ → "$0.0063"。locale は呼ぶ側の Intl の名前（en-US / ja-JP）
 * WHY 3 桁から: 1 プレイは 1 セント = 2 桁だと "$0.01" と "$0.0063" が並んで桁がそろわない。絵（企画書 §5）の "$0.010 / play" に合わせる
 */
export function ratePerPlay(rateCents: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 4 }).format(rateCents / 100);
}

/** 週の表示 "Oct 26 – Nov 1"（UTC の日付・年は出さない）。locale は Intl の名前 */
export function weekRange(start: string, end: string, locale: string): string {
  const f = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${f.format(new Date(`${start}T00:00:00Z`))} – ${f.format(new Date(`${end}T00:00:00Z`))}`;
}

/** 日付 1 つ（UTC・"Nov 2"） */
export function dayLabel(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
}

/** 締めた週の払いの状態の札の色（ui の pill の名前） */
export function weekTone(status: PlayEarnView['weeks'][number]['status']): 'ok' | 'accent' | 'warn' | '' {
  if (status === 'sent') return 'ok';
  if (status === 'ready') return 'accent';
  if (status === 'voided') return 'warn';
  return '';
}

/** 何も無い（今週の数えも、締めた週の払いも無い）= 「最初の 1 プレイを待っている」の案内を出す */
export function isEmpty(v: PlayEarnView): boolean {
  return v.mine.countedPlays === 0 && v.mine.notCounted === 0 && v.weeks.length === 0;
}

/**
 * ドルの額（広告のカードの「今週の入れ物 $500」「1 人 $100 まで」・2026-10-27 console/earn-ads-card）。ちょうどのドルは小数を出さない
 * （告知の文の書き方 = "$500" / "$100"。money() の "$500.00" だと告知と見た目が違う）。端があるときだけ 2 桁
 */
export function wholeUsd(cents: number, locale: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(cents / 100);
}
